import React, { useState } from 'react';
import { calendarService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const DoctorSchedulesJsonPanel = ({ onImported }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [mode, setMode] = useState('replace_listed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleExport = async () => {
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const data = await calendarService.exportDoctorSchedulesJson();
      const text = JSON.stringify({ ...data, mode }, null, 2);
      setJsonText(text);
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `doctor_schedules_${data.clid || 'clinic'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSuccess(t('doctorSchedulesJson.exportOk', 'Exported doctor schedules JSON.'));
    } catch (err) {
      console.error('[DoctorSchedulesJsonPanel] export failed', err);
      setError(err.response?.data?.detail || err.message || t('doctorSchedulesJson.exportFail', 'Failed to export'));
    } finally {
      setBusy(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        setJsonText(JSON.stringify(parsed, null, 2));
        if (parsed.mode) setMode(parsed.mode);
        setError(null);
      } catch (err) {
        setError(t('doctorSchedulesJson.invalidJson', 'Invalid JSON file.'));
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImport = async () => {
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const parsed = JSON.parse(jsonText);
      const doctorSchedules = parsed.doctor_schedules || parsed.clinic_doctor_schedules;
      if (!Array.isArray(doctorSchedules) || doctorSchedules.length === 0) {
        setError(t('doctorSchedulesJson.needArray', 'JSON must include a non-empty doctor_schedules array.'));
        return;
      }
      const payload = { mode: parsed.mode || mode, doctor_schedules: doctorSchedules };
      if (!window.confirm(t('doctorSchedulesJson.confirmImport', 'Import doctor schedules from JSON?'))) {
        return;
      }
      const response = await calendarService.importDoctorSchedulesJson(payload);
      setSuccess(response.message || t('doctorSchedulesJson.importOk', 'Import completed.'));
      if (onImported) onImported(response);
    } catch (err) {
      console.error('[DoctorSchedulesJsonPanel] import failed', err);
      setError(err.response?.data?.detail || err.message || t('doctorSchedulesJson.importFail', 'Failed to import'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-5 overflow-hidden rounded-xl border border-gray-200 bg-gray-50/80">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-100/80"
      >
        <span className="text-sm font-medium text-gray-800">
          {t('doctorSchedulesJson.title', 'JSON export / configure all')}
        </span>
        <span className="text-xs font-medium text-blue-600">
          {open ? t('doctorSchedulesJson.hide', 'Hide') : t('doctorSchedulesJson.show', 'Show')}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-gray-200 px-4 pb-4 pt-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleExport}
              disabled={busy}
              className="rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-neutral-800 disabled:opacity-50"
            >
              {t('doctorSchedulesJson.export', 'Export JSON')}
            </button>
            <label className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50">
              {t('doctorSchedulesJson.upload', 'Upload JSON')}
              <input type="file" accept="application/json,.json" className="hidden" onChange={handleFileUpload} />
            </label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="replace_listed">{t('doctorSchedulesJson.replaceListed', 'replace listed')}</option>
              <option value="upsert">{t('doctorSchedulesJson.upsert', 'upsert')}</option>
            </select>
            <button
              type="button"
              onClick={handleImport}
              disabled={busy || !jsonText.trim()}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
            >
              {t('doctorSchedulesJson.import', 'Import / configure')}
            </button>
          </div>
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            rows={12}
            className="w-full rounded-lg border border-gray-300 bg-white p-3 font-mono text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100">{error}</div>}
          {success && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700 ring-1 ring-emerald-100">{success}</div>}
        </div>
      )}
    </div>
  );
};

export default DoctorSchedulesJsonPanel;
